import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { 
  Loader2, User, Briefcase, Wrench, GraduationCap,
  TrendingUp, Lightbulb, ChevronRight, ChevronLeft, RefreshCw,
  Check, Mail, Phone, MapPin, Building2, Calendar, FileText,
  Shield, AlertTriangle, RotateCcw
} from 'lucide-react';
import { toast } from 'sonner';
import { TagInput } from '../components/ui/multi-select';
import { MasterSelect } from '../components/ui/master-select';
import { DatePicker } from '../components/ui/date-picker';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Track B (6 Sep 2026): the public onboarding routes need the short-lived
// upload token that /api/public/upload-resume issues (2-hour life, bound to
// the candidate id). PortalHomePage.js stores it per candidate id; the
// manual-entry path below stores its own. Sent as X-Upload-Token on every
// call for that candidate. No token = the backend answers 401 and the page
// asks the person to upload again.
const uploadTokenKey = (id) => `dcs_upload_token_${id}`;
const rememberUploadToken = (id, token) => {
  try { if (id && token) sessionStorage.setItem(uploadTokenKey(id), token); } catch (e) { /* storage blocked */ }
};
const uploadTokenHeaders = (id) => {
  try {
    const t = id ? sessionStorage.getItem(uploadTokenKey(id)) : null;
    return t ? { 'X-Upload-Token': t } : {};
  } catch (e) { return {}; }
};

// Daruwallas Logo Component
const DaruwallasLogo = ({ className = "" }) => (
  <div className={`flex items-center ${className}`}>
    <img 
      src="/logo.png"
      alt="Daruwallas"
      className="h-10 w-auto"
    />
  </div>
);

const STEPS = [
  { id: 1, key: 'personal', title: 'Personal Info', icon: User },
  { id: 2, key: 'experience', title: 'Experience', icon: Briefcase },
  { id: 3, key: 'skills', title: 'Skills', icon: Wrench },
  { id: 4, key: 'education', title: 'Education', icon: GraduationCap },
];

// Parsing fields to track
const PARSING_FIELDS = [
  'Full Name', 'Email', 'Phone Number', 'Location', 'Current Company',
  'Job Title', 'Experience', 'Technical Skills', 'Education', 'Summary',
  'Work History', 'Certifications', 'Languages', 'Salary Details',
  'Notice Period', 'LinkedIn', 'Soft Skills', 'Industry', 'Projects'
];

// Golden Electric Reading Contents Component
function ReadingContentsProgress({ progress, currentField, onRetry, failed }) {
  return (
    <div className="min-h-screen bg-[#FDFCF9] flex items-center justify-center">
      <div className="w-full max-w-md p-8">
        {/* Logo */}
        <div className="flex justify-center mb-10">
          <DaruwallasLogo />
        </div>

        {failed ? (
          /* Failure State with Retry */
          <div className="text-center">
            <div className="w-16 h-16 mx-auto rounded-full bg-red-50 flex items-center justify-center mb-4">
              <AlertTriangle className="w-8 h-8 text-red-400" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Couldn't read your resume</h2>
            <p className="text-gray-500 text-sm mb-6">Something went wrong while extracting details. You can try again with the same document.</p>
            <button
              onClick={onRetry}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-white font-medium text-sm transition-all hover:scale-105"
              style={{ background: 'linear-gradient(135deg, #C9A54E 0%, #E8C96A 100%)' }}
              data-testid="retry-parse-btn"
            >
              <RotateCcw className="w-4 h-4" />
              Retry Reading
            </button>
          </div>
        ) : (
          /* Parsing Animation - Professional Light Theme */
          <>
            <div className="text-center mb-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-1">Reading Contents</h2>
              <p className="text-gray-400 text-sm">Extracting information from your resume</p>
            </div>

            {/* Professional Electric Animation - Light Background */}
            <div className="relative h-24 mb-6 overflow-hidden rounded-2xl bg-gradient-to-br from-slate-50 via-white to-amber-50/30 border border-slate-100 shadow-sm">
              {/* Animated circuit/pulse lines */}
              <svg className="absolute inset-0 w-full h-full" viewBox="0 0 400 96" preserveAspectRatio="none">
                <defs>
                  <linearGradient id="goldGradLight" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#C9A54E" stopOpacity="0" />
                    <stop offset="30%" stopColor="#D4AF37" stopOpacity="0.8" />
                    <stop offset="50%" stopColor="#FFD700" stopOpacity="1" />
                    <stop offset="70%" stopColor="#D4AF37" stopOpacity="0.8" />
                    <stop offset="100%" stopColor="#C9A54E" stopOpacity="0" />
                  </linearGradient>
                  <filter id="glowLight" x="-50%" y="-50%" width="200%" height="200%">
                    <feGaussianBlur stdDeviation="2" result="blur" />
                    <feComposite in="SourceGraphic" in2="blur" operator="over" />
                  </filter>
                </defs>
                
                {/* Main pulse wave */}
                <path 
                  d="M0,48 Q50,30 100,48 T200,48 T300,48 T400,48" 
                  fill="none" 
                  stroke="url(#goldGradLight)" 
                  strokeWidth="2" 
                  filter="url(#glowLight)"
                  className="animate-pulse-wave"
                />
                
                {/* Secondary subtle wave */}
                <path 
                  d="M0,48 Q75,60 150,48 T300,48 T400,48" 
                  fill="none" 
                  stroke="#C9A54E" 
                  strokeWidth="1" 
                  opacity="0.3"
                  className="animate-pulse-wave-delayed"
                />
                
                {/* Energy nodes */}
                {[50, 150, 250, 350].map((x, i) => (
                  <g key={i}>
                    <circle 
                      cx={x} 
                      cy="48" 
                      r="6" 
                      fill="none" 
                      stroke="#C9A54E" 
                      strokeWidth="1.5" 
                      opacity="0.4"
                      className="animate-node-pulse"
                      style={{ animationDelay: `${i * 0.15}s` }}
                    />
                    <circle 
                      cx={x} 
                      cy="48" 
                      r="3" 
                      fill="#D4AF37"
                      className="animate-node-glow"
                      style={{ animationDelay: `${i * 0.15}s` }}
                    />
                  </g>
                ))}
              </svg>
              
              {/* Traveling light effect */}
              <div 
                className="absolute top-0 bottom-0 w-32"
                style={{
                  background: 'linear-gradient(90deg, transparent, rgba(212,175,55,0.15), transparent)',
                  animation: 'travelLight 2s ease-in-out infinite'
                }}
              />
              
              {/* Center icon */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-12 h-12 rounded-full bg-white shadow-md border border-amber-100 flex items-center justify-center">
                  <FileText className="w-5 h-5 text-amber-600 animate-pulse" />
                </div>
              </div>
            </div>

            {/* Single-line rotating field display */}
            <div className="text-center mb-6">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-slate-100 shadow-sm">
                <div className="w-2 h-2 rounded-full bg-[#C9A54E] animate-pulse" />
                <span className="text-sm font-medium text-gray-700 min-w-[140px] transition-all" data-testid="parsing-field-display">
                  {currentField || 'Initializing...'}
                </span>
              </div>
            </div>

            {/* Progress bar */}
            <div className="mb-4">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-xs font-medium text-gray-500">Extracting</span>
                <span className="text-xs font-semibold" style={{ color: '#C9A54E' }}>{progress}%</span>
              </div>
              <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                <div 
                  className="h-full rounded-full transition-all duration-500 relative overflow-hidden"
                  style={{ width: `${progress}%`, background: 'linear-gradient(90deg, #C9A54E, #E8C96A, #D4AF37)' }}
                >
                  <div 
                    className="absolute inset-0"
                    style={{
                      background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.4) 50%, transparent 100%)',
                      animation: 'shimmerBar 1.5s ease-in-out infinite'
                    }}
                  />
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* CSS Animations */}
      <style>{`
        @keyframes travelLight {
          0% { left: -8rem; }
          100% { left: calc(100% + 8rem); }
        }
        @keyframes shimmerBar {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(200%); }
        }
        .animate-pulse-wave {
          stroke-dasharray: 200;
          stroke-dashoffset: 200;
          animation: pulseWave 2s ease-in-out infinite;
        }
        .animate-pulse-wave-delayed {
          stroke-dasharray: 200;
          stroke-dashoffset: 200;
          animation: pulseWave 2.5s ease-in-out infinite 0.5s;
        }
        @keyframes pulseWave {
          0% { stroke-dashoffset: 200; opacity: 0.3; }
          50% { stroke-dashoffset: 0; opacity: 1; }
          100% { stroke-dashoffset: -200; opacity: 0.3; }
        }
        .animate-node-pulse {
          animation: nodePulse 1.5s ease-in-out infinite;
        }
        @keyframes nodePulse {
          0%, 100% { r: 6; opacity: 0.2; }
          50% { r: 10; opacity: 0.5; }
        }
        .animate-node-glow {
          animation: nodeGlow 1.5s ease-in-out infinite;
        }
        @keyframes nodeGlow {
          0%, 100% { r: 2; opacity: 0.6; }
          50% { r: 4; opacity: 1; }
        }
      `}</style>
    </div>
  );
}

// OTP Verification Component  
function OTPVerificationStep({ candidateId, phone, onVerified, onSkip }) {
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [sent, setSent] = useState(false);
  const [sentVia, setSentVia] = useState('');
  const [maskedPhone, setMaskedPhone] = useState('');
  const [testOtp, setTestOtp] = useState('');
  const [countdown, setCountdown] = useState(0);
  const inputRefs = useRef([]);

  const sendOtp = async () => {
    setSending(true);
    try {
      const res = await fetch(`${API_URL}/api/public/candidate/${candidateId}/send-otp`, { method: 'POST', headers: uploadTokenHeaders(candidateId) });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Failed to send OTP');
      }
      const data = await res.json();
      setSent(true);
      setSentVia(data.sent_via);
      setMaskedPhone(data.phone_masked);
      if (data.test_otp) setTestOtp(data.test_otp);
      setCountdown(data.otp_validity_minutes * 60);
      toast.success(data.sent_via === 'whatsapp' ? 'OTP sent to your WhatsApp!' : 'OTP generated!');
    } catch (e) {
      toast.error(e.message);
    }
    setSending(false);
  };

  useEffect(() => {
    if (phone) sendOtp();
  }, []);

  useEffect(() => {
    if (countdown <= 0) return;
    const timer = setInterval(() => setCountdown(c => c - 1), 1000);
    return () => clearInterval(timer);
  }, [countdown]);

  const handleOtpChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value.slice(-1);
    setOtp(newOtp);
    
    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
    
    // Auto-submit when all 6 digits are entered
    if (value && index === 5) {
      const otpString = newOtp.join('');
      if (otpString.length === 6) {
        // Small delay to show the last digit before submitting
        setTimeout(() => {
          verifyOtpWithValue(otpString);
        }, 150);
      }
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length === 6) {
      setOtp(pasted.split(''));
      inputRefs.current[5]?.focus();
      // Auto-submit on paste
      setTimeout(() => {
        verifyOtpWithValue(pasted);
      }, 150);
    }
  };

  const verifyOtpWithValue = async (otpStr) => {
    if (otpStr.length !== 6) { toast.error('Please enter the full 6-digit OTP'); return; }
    
    setVerifying(true);
    try {
      const res = await fetch(`${API_URL}/api/public/candidate/${candidateId}/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...uploadTokenHeaders(candidateId) },
        body: JSON.stringify({ otp: otpStr })
      });
      
      // Clone response before reading to avoid "body locked" error
      const resClone = res.clone();
      
      if (!res.ok) {
        try {
          const err = await res.json();
          throw new Error(err.detail || 'Verification failed');
        } catch (parseError) {
          const text = await resClone.text();
          throw new Error(text || 'Verification failed');
        }
      }
      const data = await res.json();
      toast.success(data.message);
      onVerified();
    } catch (e) {
      toast.error(e.message);
      setOtp(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    }
    setVerifying(false);
  };

  const verifyOtp = async () => {
    const otpStr = otp.join('');
    await verifyOtpWithValue(otpStr);
  };

  const formatTime = (s) => `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}`;

  return (
    <div className="min-h-screen bg-[#FDFCF9] flex items-center justify-center">
      <div className="w-full max-w-md p-8">
        <div className="flex justify-center mb-8">
          <DaruwallasLogo />
        </div>

        <div className="text-center mb-8">
          <div className="w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-4" style={{ background: 'linear-gradient(135deg, #C9A54E20, #E8C96A20)' }}>
            <Shield className="w-8 h-8" style={{ color: '#C9A54E' }} />
          </div>
          <h2 className="text-2xl font-semibold text-gray-900 mb-2">Verify Your Phone</h2>
          <p className="text-gray-500 text-sm">
            {sent 
              ? <>We sent a 6-digit OTP to <span className="font-medium text-gray-700">{maskedPhone}</span></>
              : 'Sending verification code to your phone number...'
            }
          </p>
        </div>

        {sending && !sent ? (
          <div className="flex justify-center py-8">
            <Loader2 className="w-8 h-8 animate-spin" style={{ color: '#C9A54E' }} />
          </div>
        ) : (
          <>
            {/* OTP Input */}
            <div className="flex justify-center gap-3 mb-6" onPaste={handlePaste}>
              {otp.map((digit, i) => (
                <input
                  key={i}
                  ref={el => inputRefs.current[i] = el}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleOtpChange(i, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(i, e)}
                  className="w-12 h-14 text-center text-xl font-semibold rounded-xl border-2 border-gray-200 focus:border-[#C9A54E] focus:ring-1 focus:ring-[#C9A54E] outline-none transition-all"
                  data-testid={`otp-input-${i}`}
                />
              ))}
            </div>

            {/* Verify Button */}
            <button
              onClick={verifyOtp}
              disabled={verifying || otp.join('').length !== 6}
              className="w-full py-3 rounded-full text-white font-medium text-sm transition-all hover:scale-[1.02] disabled:opacity-50 disabled:hover:scale-100"
              style={{ background: 'linear-gradient(135deg, #C9A54E 0%, #E8C96A 100%)' }}
              data-testid="verify-otp-btn"
            >
              {verifying ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : 'Verify & Continue'}
            </button>

            {/* Resend / Timer */}
            <div className="text-center mt-4">
              {countdown > 0 ? (
                <p className="text-xs text-gray-400">Resend in {formatTime(countdown)}</p>
              ) : (
                <button onClick={sendOtp} disabled={sending} className="text-sm font-medium hover:underline" style={{ color: '#C9A54E' }}>
                  Resend OTP
                </button>
              )}
            </div>
          </>
        )}
      </div>

      {/* CSS Animations */}
      <style>{`
        @keyframes electricFlow {
          0% { stroke-dashoffset: 800; }
          50% { stroke-dashoffset: 0; }
          100% { stroke-dashoffset: -800; }
        }
        @keyframes sparkle {
          0%, 100% { opacity: 0.3; r: 2; }
          50% { opacity: 1; r: 4; }
        }
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
      `}</style>
    </div>
  );
}

// Profile Completeness Component
function ProfileCompleteness({ percentage }) {
  const getMessage = () => {
    if (percentage >= 90) return 'Excellent! Your profile stands out to recruiters.';
    if (percentage >= 70) return 'Great progress! Add more to unlock better matches.';
    if (percentage >= 40) return 'Good start! Keep going to attract top opportunities.';
    return 'Just getting started. Complete your profile to unlock opportunities!';
  };

  const getColor = () => {
    if (percentage >= 90) return '#22c55e';
    if (percentage >= 70) return '#C9A54E';
    if (percentage >= 40) return '#f59e0b';
    return '#ef4444';
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-base font-semibold text-gray-900">Profile Completeness</h3>
        <span className="text-2xl font-bold" style={{ color: getColor() }}>{percentage}%</span>
      </div>
      
      <div className="h-2 bg-gray-100 rounded-full overflow-hidden mb-4">
        <div
          className="h-full rounded-full transition-all duration-500"
          style={{ width: `${percentage}%`, backgroundColor: getColor() }}
        />
      </div>

      <div className="flex items-start gap-2 mb-3" style={{ color: getColor() }}>
        <TrendingUp className="w-4 h-4 shrink-0 mt-0.5" />
        <p className="text-sm">{getMessage()}</p>
      </div>

      {percentage < 90 && (
        <div className="flex items-start gap-2 p-3 bg-amber-50 rounded-lg border border-amber-100">
          <Lightbulb className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
          <p className="text-xs text-amber-700">
            <span className="font-semibold">Pro Tip:</span> Profiles with 90%+ completeness are shown to top recruiters first and get 5x more interview calls!
          </p>
        </div>
      )}
    </div>
  );
}

// Step Navigation Component
function StepNavigation({ currentStep, onStepClick }) {
  return (
    <div className="border-b border-gray-100 bg-white">
      <div className="max-w-4xl mx-auto px-6">
        <div className="flex items-center justify-between py-4">
          {STEPS.map((step, index) => {
            const StepIcon = step.icon;
            const isActive = currentStep === step.id;
            const isCompleted = currentStep > step.id;
            
            return (
              <button
                key={step.id}
                onClick={() => step.id <= currentStep && onStepClick(step.id)}
                disabled={step.id > currentStep}
                className={`
                  flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all
                  ${isActive 
                    ? 'bg-[#C9A54E]/10 text-[#C9A54E]' 
                    : isCompleted 
                      ? 'text-gray-600 hover:text-gray-900' 
                      : 'text-gray-400 cursor-not-allowed'
                  }
                `}
              >
                <StepIcon className="w-4 h-4" />
                <span className="hidden sm:inline">{step.title}</span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default function PortalOnboardingPage() {
  const { candidateId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const isManual = searchParams.get('manual') === 'true' || candidateId === 'new';
  
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(!isManual);
  const [parsing, setParsing] = useState(!isManual);
  const [parsingProgress, setParsingProgress] = useState(0);
  const [parsingFailed, setParsingFailed] = useState(false);
  const [currentParsingField, setCurrentParsingField] = useState('');
  const [regenerating, setRegenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [completeness, setCompleteness] = useState({ percentage: 0 });
  const [actualCandidateId, setActualCandidateId] = useState(isManual ? null : candidateId);
  const [showOtpVerification, setShowOtpVerification] = useState(false);
  const [phoneVerified, setPhoneVerified] = useState(false);
  
  const [formData, setFormData] = useState({
    // Personal
    name: '',
    email: '',
    phone: '',
    date_of_birth: '',
    gender: '',
    nationality: '',
    current_location: '',
    linkedin_url: '',
    
    // Experience
    current_designation: '',
    current_company: '',
    total_years_experience: '',
    current_salary: '',
    expected_salary: '',
    notice_period_days: '',
    professional_summary: '',
    
    // Skills
    technical_skills: [],
    soft_skills: [],
    languages: [],
    certifications: [],
    
    // Education
    education: []
  });

  // Create blank candidate for manual entry
  useEffect(() => {
    if (isManual && !actualCandidateId) {
      setLoading(false);
      setParsing(false);
    }
  }, [isManual, actualCandidateId]);

  // Simulate progress animation with field cycling
  useEffect(() => {
    if (parsing && !isManual) {
      let fieldIdx = 0;
      const progressInterval = setInterval(() => {
        setParsingProgress(prev => {
          if (prev >= 90) return prev;
          return prev + Math.random() * 15;
        });
      }, 500);
      
      const fieldInterval = setInterval(() => {
        setCurrentParsingField(PARSING_FIELDS[fieldIdx % PARSING_FIELDS.length]);
        fieldIdx++;
      }, 400);
      
      return () => { clearInterval(progressInterval); clearInterval(fieldInterval); };
    }
  }, [parsing, isManual]);

  // Poll for parsing status
  const checkParsingStatus = useCallback(async () => {
    if (!actualCandidateId || isManual) return true;
    
    try {
      const response = await fetch(`${API_URL}/api/public/candidate/${actualCandidateId}/parsed`, { headers: uploadTokenHeaders(actualCandidateId) });
      if (response.status === 401 || response.status === 403) {
        setParsing(false);
        setParsingFailed(true);
        toast.error('Your upload session has expired. Please upload your resume again.');
        return true;
      }
      if (!response.ok) throw new Error('Failed to fetch');
      
      const data = await response.json();
      setCompleteness(data.completeness || { percentage: 0 });
      
      if (data.parsing_status === 'completed' || data.parsing_status === 'skipped') {
        setParsingProgress(100);
        const candidate = data.candidate;
        
        // Small delay to show completion
        setTimeout(() => {
          setParsing(false);
          setFormData(prev => ({
            ...prev,
            name: candidate.name || '',
            email: candidate.email || '',
            phone: candidate.phone || '',
            date_of_birth: candidate.date_of_birth || '',
            gender: candidate.gender || '',
            nationality: candidate.nationality || '',
            current_location: candidate.current_location || '',
            linkedin_url: candidate.linkedin_url || '',
            current_designation: candidate.current_designation || '',
            current_company: candidate.current_company || '',
            total_years_experience: candidate.total_years_experience || '',
            current_salary: candidate.current_salary || '',
            expected_salary: candidate.expected_salary || '',
            notice_period_days: candidate.notice_period_days || '',
            professional_summary: candidate.professional_summary || '',
            technical_skills: candidate.technical_skills || [],
            soft_skills: candidate.soft_skills || [],
            languages: candidate.languages || [],
            // Normalize certifications - convert objects to strings
            certifications: (candidate.certifications || []).map(cert => 
              typeof cert === 'string' ? cert : (cert.certification_name || cert.name || JSON.stringify(cert))
            ),
            education: candidate.education || []
          }));
          setLoading(false);
          
          // Check if already phone verified
          if (candidate.phone_verified) {
            setPhoneVerified(true);
            setShowOtpVerification(false);
          } else if (candidate.phone) {
            // Show OTP verification if phone number was found but not verified
            setShowOtpVerification(true);
          }
        }, 800);
        
        return true;
      } else if (data.parsing_status === 'failed') {
        setParsing(false);
        setParsingFailed(true);
        setLoading(false);
        return true;
      } else if (data.parsing_status === 'parsing') {
        // Still parsing
      }
      
      return false;
    } catch (error) {
      console.error('Polling error:', error);
      return false;
    }
  }, [actualCandidateId, isManual]);

  useEffect(() => {
    if (isManual) return;
    
    setParsing(true);
    
    const pollInterval = setInterval(async () => {
      const done = await checkParsingStatus();
      if (done) clearInterval(pollInterval);
    }, 2000);
    
    checkParsingStatus();
    
    return () => clearInterval(pollInterval);
  }, [checkParsingStatus, isManual]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handlePhoneChange = (value) => {
    let digits = value.replace(/\D/g, '');
    
    if (digits.startsWith('0091')) digits = digits.slice(4);
    else if (digits.startsWith('91') && digits.length > 10) digits = digits.slice(2);
    else if (digits.startsWith('0') && digits.length === 11) digits = digits.slice(1);
    
    if (digits.length > 10) digits = digits.slice(-10);
    
    setFormData(prev => ({ ...prev, phone: digits }));
  };

  const handleRegenerate = async () => {
    if (!actualCandidateId) return;
    
    setRegenerating(true);
    setParsingProgress(0);
    setCurrentParsingField('');
    setParsingFailed(false);
    
    try {
      const response = await fetch(`${API_URL}/api/public/candidate/${actualCandidateId}/regenerate`, {
        method: 'POST',
        headers: uploadTokenHeaders(actualCandidateId)
      });
      
      if (!response.ok) throw new Error('Regenerate failed');
      
      toast.success('Re-reading your resume...');
      setParsing(true);
      
      const pollInterval = setInterval(async () => {
        const done = await checkParsingStatus();
        if (done) {
          clearInterval(pollInterval);
          setRegenerating(false);
        }
      }, 2000);
      
    } catch (error) {
      toast.error('Failed to regenerate. Please try again.');
      setRegenerating(false);
    }
  };

  const handleSaveStep = async () => {
    if (!actualCandidateId && !isManual) return;
    
    setSaving(true);
    try {
      if (!actualCandidateId) {
        const createResponse = await fetch(`${API_URL}/api/public/upload-resume`, {
          method: 'POST',
          body: (() => {
            const fd = new FormData();
            const blob = new Blob(['Manual entry'], { type: 'text/plain' });
            fd.append('file', blob, 'manual.txt');
            fd.append('source', 'portal_manual');
            return fd;
          })()
        });
        
        if (createResponse.ok) {
          const createData = await createResponse.json();
          rememberUploadToken(createData.candidate_id, createData.upload_token);
          setActualCandidateId(createData.candidate_id);
          
          await fetch(`${API_URL}/api/public/candidate/${createData.candidate_id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json', ...uploadTokenHeaders(createData.candidate_id) },
            body: JSON.stringify(formData)
          });
        }
      } else {
        const response = await fetch(`${API_URL}/api/public/candidate/${actualCandidateId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', ...uploadTokenHeaders(actualCandidateId) },
          body: JSON.stringify(formData)
        });
        
        if (response.ok) {
          const data = await response.json();
          setCompleteness(data.completeness || { percentage: 0 });
        }
      }
    } catch (error) {
      toast.error('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleNext = async () => {
    await handleSaveStep();
    if (currentStep < 4) {
      setCurrentStep(prev => prev + 1);
      window.scrollTo(0, 0);
    }
  };

  const handlePrev = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
      window.scrollTo(0, 0);
    }
  };

  // OTP-only registration - no password required
  const handleFinishOnboarding = async () => {
    if (!formData.email) {
      toast.error('Email is required');
      return;
    }

    setSaving(true);
    try {
      // Save current step data first
      if (actualCandidateId) {
        await fetch(`${API_URL}/api/public/candidate/${actualCandidateId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', ...uploadTokenHeaders(actualCandidateId) },
          body: JSON.stringify(formData)
        });
      }
      
      if (!actualCandidateId) {
        toast.error('Please complete previous steps first');
        setSaving(false);
        return;
      }
      
      // Register without password (OTP-only login)
      const response = await fetch(`${API_URL}/api/portal/candidate/${actualCandidateId}/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...uploadTokenHeaders(actualCandidateId) },
        body: JSON.stringify({
          email: formData.email,
          name: formData.name || 'Candidate'
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMsg = errorData.detail || 'Registration failed';
        throw new Error(errorMsg);
      }
      
      const data = await response.json();
      
      localStorage.setItem('candidate_token', data.candidate_token || data.access_token);
      localStorage.setItem('candidate_id', data.candidate_id);
      
      toast.success('Profile completed! Welcome to Daruwallas!');
      navigate('/my');
      
    } catch (error) {
      toast.error(error.message || 'Failed to complete profile');
    } finally {
      setSaving(false);
    }
  };

  // Show Reading Contents progress during parsing
  if (loading || parsing || parsingFailed) {
    return (
      <ReadingContentsProgress 
        progress={Math.min(Math.round(parsingProgress), 100)} 
        currentField={currentParsingField}
        failed={parsingFailed}
        onRetry={handleRegenerate}
      />
    );
  }

  // Show OTP verification step
  if (showOtpVerification && !phoneVerified) {
    return (
      <OTPVerificationStep
        candidateId={actualCandidateId}
        phone={formData.phone}
        onVerified={() => { setPhoneVerified(true); setShowOtpVerification(false); }}
        onSkip={() => setShowOtpVerification(false)}
      />
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFCF9]">
      {/* Header */}
      <header className="bg-white border-b border-gray-100">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <DaruwallasLogo />
          <span className="text-sm text-gray-500">Step {currentStep} of 4</span>
        </div>
      </header>

      {/* Step Navigation */}
      <StepNavigation currentStep={currentStep} onStepClick={setCurrentStep} />

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-6 py-8">
        {/* Profile Completeness */}
        <ProfileCompleteness percentage={completeness.percentage || 0} />

        {/* Form Card */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 sm:p-8">
          {/* Step 1: Personal Info */}
          {currentStep === 1 && (
            <div className="space-y-6">
              <div>
                <h2 className="text-xl font-bold text-gray-900">Personal Information</h2>
                <p className="text-sm text-gray-500 mt-1">Tell us about yourself</p>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
                  <input 
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleChange('name', e.target.value)}
                    placeholder="Your full name"
                    className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-[#C9A54E] focus:ring-1 focus:ring-[#C9A54E] outline-none transition-colors"
                  />
                </div>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                    <input 
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleChange('email', e.target.value)}
                      placeholder="you@example.com"
                      className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-[#C9A54E] focus:ring-1 focus:ring-[#C9A54E] outline-none transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                    <input 
                      type="text"
                      value={formData.phone}
                      onChange={(e) => handlePhoneChange(e.target.value)}
                      placeholder="10 digit mobile number"
                      maxLength={10}
                      className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-[#C9A54E] focus:ring-1 focus:ring-[#C9A54E] outline-none transition-colors"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Date of Birth</label>
                    <DatePicker
                      value={formData.date_of_birth}
                      onChange={(date) => handleChange('date_of_birth', date)}
                      placeholder="Select your birth date"
                      minYear={1940}
                      maxYear={new Date().getFullYear() - 15}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Gender</label>
                    <Select 
                      value={formData.gender || ''} 
                      onValueChange={(value) => handleChange('gender', value)}
                    >
                      <SelectTrigger className="w-full h-11">
                        <SelectValue placeholder="Select gender" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Male">Male</SelectItem>
                        <SelectItem value="Female">Female</SelectItem>
                        <SelectItem value="Other">Other</SelectItem>
                        <SelectItem value="Prefer not to say">Prefer not to say</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Nationality</label>
                    <Select 
                      value={formData.nationality || ''} 
                      onValueChange={(value) => handleChange('nationality', value)}
                    >
                      <SelectTrigger className="w-full h-11">
                        <SelectValue placeholder="Select nationality" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Indian">Indian</SelectItem>
                        <SelectItem value="American">American</SelectItem>
                        <SelectItem value="British">British</SelectItem>
                        <SelectItem value="Canadian">Canadian</SelectItem>
                        <SelectItem value="Australian">Australian</SelectItem>
                        <SelectItem value="German">German</SelectItem>
                        <SelectItem value="French">French</SelectItem>
                        <SelectItem value="Chinese">Chinese</SelectItem>
                        <SelectItem value="Japanese">Japanese</SelectItem>
                        <SelectItem value="Other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Current Location</label>
                    <input 
                      type="text"
                      value={formData.current_location}
                      onChange={(e) => handleChange('current_location', e.target.value)}
                      placeholder="e.g. Mumbai"
                      className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-[#C9A54E] focus:ring-1 focus:ring-[#C9A54E] outline-none transition-colors"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">LinkedIn URL</label>
                  <input 
                    type="text"
                    value={formData.linkedin_url}
                    onChange={(e) => handleChange('linkedin_url', e.target.value)}
                    placeholder="https://linkedin.com/in/..."
                    className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-[#C9A54E] focus:ring-1 focus:ring-[#C9A54E] outline-none transition-colors"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Experience */}
          {currentStep === 2 && (
            <div className="space-y-6">
              <div>
                <h2 className="text-xl font-bold text-gray-900">Work Experience</h2>
                <p className="text-sm text-gray-500 mt-1">Tell us about your professional background</p>
              </div>
              
              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Current Designation</label>
                    <input 
                      type="text"
                      value={formData.current_designation}
                      onChange={(e) => handleChange('current_designation', e.target.value)}
                      placeholder="e.g. Senior Software Engineer"
                      className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-[#C9A54E] focus:ring-1 focus:ring-[#C9A54E] outline-none transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Current Company</label>
                    <input 
                      type="text"
                      value={formData.current_company}
                      onChange={(e) => handleChange('current_company', e.target.value)}
                      placeholder="Company name"
                      className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-[#C9A54E] focus:ring-1 focus:ring-[#C9A54E] outline-none transition-colors"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Total Experience (years)</label>
                  <input 
                    type="number"
                    value={formData.total_years_experience}
                    onChange={(e) => handleChange('total_years_experience', e.target.value)}
                    placeholder="e.g. 5"
                    className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-[#C9A54E] focus:ring-1 focus:ring-[#C9A54E] outline-none transition-colors"
                  />
                </div>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Current CTC (INR)</label>
                    <input 
                      type="text"
                      value={formData.current_salary}
                      onChange={(e) => handleChange('current_salary', e.target.value)}
                      placeholder="e.g. 12,00,000"
                      className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-[#C9A54E] focus:ring-1 focus:ring-[#C9A54E] outline-none transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Expected CTC (INR)</label>
                    <input 
                      type="text"
                      value={formData.expected_salary}
                      onChange={(e) => handleChange('expected_salary', e.target.value)}
                      placeholder="e.g. 18,00,000"
                      className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-[#C9A54E] focus:ring-1 focus:ring-[#C9A54E] outline-none transition-colors"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notice Period (days)</label>
                  <input 
                    type="number"
                    value={formData.notice_period_days}
                    onChange={(e) => handleChange('notice_period_days', e.target.value)}
                    placeholder="e.g. 30"
                    className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-[#C9A54E] focus:ring-1 focus:ring-[#C9A54E] outline-none transition-colors"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Professional Summary</label>
                  <textarea 
                    rows={4}
                    value={formData.professional_summary}
                    onChange={(e) => handleChange('professional_summary', e.target.value)}
                    placeholder="Brief summary of your professional background..."
                    className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-[#C9A54E] focus:ring-1 focus:ring-[#C9A54E] outline-none transition-colors resize-none"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Skills */}
          {currentStep === 3 && (
            <div className="space-y-6">
              <div>
                <h2 className="text-xl font-bold text-gray-900">Skills & Expertise</h2>
                <p className="text-sm text-gray-500 mt-1">Select or add your skills. Start typing to search from suggestions.</p>
              </div>
              
              <div className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Technical Skills</label>
                  <MasterSelect
                    masterDb="skills"
                    selected={formData.technical_skills || []}
                    onChange={(skills) => handleChange('technical_skills', skills)}
                    placeholder="Search or type skills (e.g., React, Python, AWS)"
                    allowCustom={true}
                    tagVariant="skill"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Soft Skills</label>
                  <TagInput
                    value={formData.soft_skills || []}
                    onChange={(skills) => handleChange('soft_skills', skills)}
                    placeholder="Type a skill and press Enter (e.g., Leadership, Communication)"
                    tagVariant="secondary"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Languages</label>
                  <MasterSelect
                    masterDb="languages"
                    selected={formData.languages || formData.language_proficiency || []}
                    onChange={(langs) => handleChange('languages', langs)}
                    placeholder="Search or type languages (e.g., English, Hindi)"
                    allowCustom={true}
                    tagVariant="language"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Certifications</label>
                  <TagInput
                    value={formData.certifications || []}
                    onChange={(certs) => handleChange('certifications', certs)}
                    placeholder="Type a certification and press Enter (e.g., AWS Certified, PMP)"
                    tagVariant="colorful"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Step 4: Education */}
          {currentStep === 4 && (
            <div className="space-y-6">
              <div>
                <h2 className="text-xl font-bold text-gray-900">Education</h2>
                <p className="text-sm text-gray-500 mt-1">Your academic qualifications</p>
              </div>
              
              {formData.education && formData.education.length > 0 ? (
                <div className="space-y-3">
                  {formData.education.map((edu, i) => (
                    <div key={i} className="p-4 bg-gray-50 rounded-lg border border-gray-100">
                      <div className="flex items-start gap-3">
                        <GraduationCap className="w-5 h-5 text-[#C9A54E] mt-0.5" />
                        <div>
                          <p className="font-medium text-gray-900">
                            {edu.degree_type} {edu.field_of_study && `in ${edu.field_of_study}`}
                          </p>
                          <p className="text-sm text-gray-600">{edu.institution_name}</p>
                          <p className="text-xs text-gray-500 mt-1">
                            {edu.graduation_year} {edu.percentage_cgpa && `• ${edu.percentage_cgpa}`}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-8 bg-gray-50 rounded-lg border border-dashed border-gray-200 text-center">
                  <GraduationCap className="w-10 h-10 mx-auto text-gray-300 mb-3" />
                  <p className="text-gray-500">No education details found</p>
                  <p className="text-xs text-gray-400 mt-1">Education will be extracted from your resume</p>
                </div>
              )}

              {actualCandidateId && (
                <button
                  onClick={handleRegenerate}
                  disabled={regenerating}
                  className="inline-flex items-center gap-2 text-sm text-[#C9A54E] hover:text-[#B8943D] transition-colors"
                >
                  {regenerating ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <RefreshCw className="w-4 h-4" />
                  )}
                  Re-read resume to extract education
                </button>
              )}
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex items-center justify-between mt-8 pt-6 border-t border-gray-100">
            <button
              onClick={handlePrev}
              disabled={currentStep === 1}
              className={`
                inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium transition-colors
                ${currentStep === 1 
                  ? 'text-gray-300 cursor-not-allowed' 
                  : 'text-gray-600 hover:text-gray-900'
                }
              `}
            >
              <ChevronLeft className="w-4 h-4" />
              Back
            </button>
            
            {currentStep < 4 ? (
              <button
                onClick={handleNext}
                disabled={saving}
                className="inline-flex items-center gap-2 px-6 py-2.5 rounded-lg text-sm font-medium text-white bg-[#C9A54E] hover:bg-[#B8943D] transition-colors disabled:opacity-50"
                data-testid="onboarding-next-btn"
              >
                {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                Next
                <ChevronRight className="w-4 h-4" />
              </button>
            ) : (
              <button
                onClick={handleFinishOnboarding}
                disabled={saving || !formData.email}
                className="inline-flex items-center gap-2 px-6 py-2.5 rounded-lg text-sm font-medium text-white bg-[#C9A54E] hover:bg-[#B8943D] transition-colors disabled:opacity-50"
                data-testid="finish-onboarding-btn"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                Finish & Go to Dashboard
              </button>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
